import { useState, useEffect } from 'react'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import PaymentSchedule from '../components/PaymentSchedule'
import DebtPaymentTotals from '../components/DebtPaymentTotals'
import { useResponseMessages } from "../hooks/useResponseMessage";

const CreditCardPaymentCalculator = ({account, hideTotals}) => {
    const [valsAssigned, setValsAssigned] = useState(false)

    const [currentBalance, setCurrentBalance] = useState(null)
    const [interestRate, setInterestRate] = useState(null)
    const [minPaymentPercOfBalance, setMinPaymentPercOfBalance] = useState(null)
    const [plusInterest, setPlusInterest] = useState(null)
    const [minPaymentForLowBalance, setMinPaymentForLowBalance] = useState(null)
    const [fixedPayment, setFixedPayment] = useState(null)
    const [zeroPercentIntroPeriod, setZeroPercentIntroPeriod] = useState(null)

    const [isLoading, setIsLoading] = useState(false)
    const [outputData, setOutputData] = useState()
    const [addExtraPayments, setAddExtraPayments] = useState(false)
    const [extraPayments, setExtraPayments] = useState([])
    const { addMessage } = useResponseMessages();

    const defaultVals = {
        currentBalance: 2000,
        interestRate: .1999,
        minPaymentPercOfBalance: .025,
        plusInterest: false,
        minPaymentForLowBalance: 40,
        fixedPayment: 0,
        zeroPercentIntroPeriod: 0,
    }

    const setValsFromAccountOrDefault = () => {
        setCurrentBalance(account ? account.balance : defaultVals.currentBalance)
        setInterestRate(account ? account.apr : defaultVals.interestRate)
        setMinPaymentPercOfBalance(account ? account.minimum_payment_percentage : defaultVals.minPaymentPercOfBalance)
        setPlusInterest(account ? false : defaultVals.plusInterest)
        setMinPaymentForLowBalance(account ? account.minimum_payment_low_balance : defaultVals.minPaymentForLowBalance)
        setFixedPayment(account ? account.fixed_payment : defaultVals.fixedPayment)
        setZeroPercentIntroPeriod(account ? 0 : defaultVals.zeroPercentIntroPeriod)

        setValsAssigned(true)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setIsLoading(true)
        getPaymentSchedule()
    }

    const getPaymentSchedule = (e) => {
        let paymentSchedule = []
        let prevBalance = currentBalance
        let paymentNumber = 0
        let runs = 0; // avoid runaway loop
        let date = new Date()

        while (prevBalance > 0 && runs < 10000) {
            paymentNumber++

            const rowVals = getPaymentRow(
                paymentNumber,
                date,
                prevBalance,
                zeroPercentIntroPeriod,
                interestRate,
                fixedPayment,
                minPaymentPercOfBalance,
                plusInterest,
                minPaymentForLowBalance,
                addExtraPayments ? extraPayments : null
            )

            if (rowVals.hasOwnProperty('error')) {
                addMessage({
                    error: rowVals.error
                })

                setIsLoading(false)

                return false
            }

            paymentSchedule.push({
                ...rowVals,
                paymentNumber,
            })

            prevBalance = rowVals.balance
            runs++;
        }

        setOutputData(paymentSchedule)
        setIsLoading(false)
    }

    useEffect(() => {
        if (!valsAssigned) {
            setValsFromAccountOrDefault()
        }
        else {
            getPaymentSchedule()
        }
    }, [valsAssigned])

    return (
        valsAssigned ? (
            <>
                <h1 className="text-center">Credit Card Payment Calculator</h1>
                <div className="standardFormWrap">
                    <form
                        className="formStandard"
                        id="CreditCardPaymentCalculatorForm"
                        onSubmit={handleSubmit}
                    >
                        <div className="formField">
                            <label>Balance: </label>
                            <input
                                type="number"
                                defaultValue={currentBalance}
                                step="any"
                                onChange={(e) =>
                                    setCurrentBalance(parseFloat(e.target.value))
                                }
                            />
                        </div>
    
                        <div className="formField">
                            <label>
                                Interest Rate: [{' '}
                                {interestRate &&
                                    parseFloat(interestRate * 100).toFixed(2) +
                                        '%'}{' '}
                                ]
                            </label>
                            <input
                                type="number"
                                defaultValue={interestRate * 100}
                                step=".01"
                                min="0"
                                max="100"
                                onChange={(e) =>
                                    setInterestRate(parseFloat(e.target.value) / 100)
                                }
                            />
                        </div>
    
                        <div className="formField">
                            <label>
                                Min % of Balance: [{' '}
                                {minPaymentPercOfBalance &&
                                    parseFloat(minPaymentPercOfBalance * 100).toFixed(
                                        2
                                    ) + '%'}{' '}
                                ]
                            </label>
                            <input
                                type="number"
                                defaultValue={minPaymentPercOfBalance * 100}
                                step=".01"
                                min="0"
                                max="100"
                                onChange={(e) =>
                                    setMinPaymentPercOfBalance(
                                        parseFloat(e.target.value) / 100
                                    )
                                }
                            />
                        </div>
    
                        <div className="formField formField-inline-checkbox size-w-auto">
                            <input
                                id="plusInterest"
                                type="checkbox"
                                defaultChecked={plusInterest}
                                onChange={(e) =>
                                    setPlusInterest(e.target.checked ? true : false)
                                }
                            />
                            <label htmlFor="plusInterest">Plus Interest?</label>
                        </div>
    
                        <div className="formField">
                            <label>Min Payment for Low Balance: </label>
                            <input
                                type="number"
                                defaultValue={minPaymentForLowBalance}
                                step="any"
                                required
                                onChange={(e) =>
                                    setMinPaymentForLowBalance(
                                        parseFloat(e.target.value)
                                    )
                                }
                            />
                        </div>
    
                        <div className="formField">
                            <label>Fixed Monthly Payment: (optional)</label>
                            <input
                                type="number"
                                defaultValue={fixedPayment}
                                step="any"
                                min={0}
                                onChange={(e) =>
                                    setFixedPayment(parseFloat(e.target.value))
                                }
                            />
                        </div>
    
                        <div className="formField">
                            <label>0% Intro Period: (months)</label>
                            <input
                                type="number"
                                defaultValue={zeroPercentIntroPeriod}
                                step="1"
                                onChange={(e) =>
                                    setZeroPercentIntroPeriod(
                                        parseFloat(e.target.value)
                                    )
                                }
                            />
                        </div>
    
                        <div className="formField formField-inline-checkbox size-w-auto">
                            <input
                                id="addExtraPayments"
                                type="checkbox"
                                defaultChecked={addExtraPayments}
                                onChange={(e) =>
                                    setAddExtraPayments(e.target.checked ? true : false)
                                }
                            />
                            <label htmlFor="addExtraPayments">Add Extra Payments:</label>
                        </div>
    
                        {
                            addExtraPayments && (
                                <div className="formField">
                                    <label>Extra Payments:</label>
                                    <table className="table-default" style={{width: "100%", border: "1px solid #d1d5db"}}>
                                        <thead>
                                            <tr>
                                                <th>Payment Number</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                extraPayments.map((payment, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    value={payment.paymentNumber}
                                                                    step="1"
                                                                    min="1"
                                                                    onChange={(e) => {
                                                                        const newExtraPayments = [...extraPayments]
                                                                        newExtraPayments[index] = {
                                                                            ...newExtraPayments[index],
                                                                            paymentNumber: parseInt(e.target.value)
                                                                        }
    
                                                                        setExtraPayments(newExtraPayments)
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    value={payment.amount}
                                                                    step="any"
                                                                    onChange={(e) => {
                                                                        const newExtraPayments = [...extraPayments]
                                                                        newExtraPayments[index] = {
                                                                            ...newExtraPayments[index],
                                                                            amount: parseFloat(e.target.value)
                                                                        }
    
                                                                        setExtraPayments(newExtraPayments)
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="button button-slim button-red"
                                                                    onClick={() => {
                                                                        // remove the extra payment
                                                                        const newExtraPayments = [...extraPayments]
                                                                        newExtraPayments.splice(index, 1)
                                                                        setExtraPayments(newExtraPayments)
                                                                    }}
                                                                >
                                                                    x
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </table>
                                    <button
                                        type="button"
                                        className="button button-slim button-blue" style={{width: "100%"}}
                                        onClick={() => setExtraPayments([...extraPayments, 0])}
                                    >
                                        +
                                    </button>
                                </div>
                            )
                        }
    
                        <div className="formField spc-m-t-1">
                            {
                                isLoading ? (
                                    <LoadingSpinner />
                                ) : (
                                    <button type="submit" className="button button-green">
                                        Get Payoff
                                    </button>
                                )
                            }
                        </div>
                    </form>
                </div>

                <div id="outputWrapper" style={{margin: "0 auto"}}>
                    {outputData && !hideTotals && <DebtPaymentTotals data={outputData} /> }
    
                    {outputData && <PaymentSchedule data={outputData} account={account} />}
                </div>
            </>
        ) : null
    )
}

const getPaymentRow = (
    paymentNumber,
    date,
    prevBalance,
    zeroPercentIntroPeriod,
    interestRate,
    fixedPayment,
    minPaymentPercOfBalance,
    plusInterest,
    minPaymentForLowBalance,
    extraPayments
) => {
    let rate
    let payment
    let totalPayment
    let interestPaid
    let principalPaid
    let balance
    let extraPayment = extraPayments
        ? (
            extraPayments.find((row) => row.paymentNumber === paymentNumber) || { amount: 0 }
        ).amount || 0
        : (
            0
        )

    // Date Column

    date = date.setMonth(date.getMonth() + 1)

    // Rate Column

    rate = paymentNumber <= zeroPercentIntroPeriod ? 0 : interestRate

    // Interest Paid Column

    interestPaid = (rate / 12) * prevBalance

    // Payment Column

    if (
        !fixedPayment ||
        fixedPayment === 0
    ) {
        payment = Math.min(
            Math.max(
                minPaymentPercOfBalance * prevBalance +
                    (plusInterest ? interestPaid : 0),
                minPaymentForLowBalance
            ),
            interestPaid + prevBalance
        )
    } else {
        payment = Math.min(fixedPayment, prevBalance + interestPaid)
    }

    // Total Payment Column

    totalPayment = payment + extraPayment

    // Principal Paid Column

    principalPaid = payment + extraPayment - interestPaid

    // Balance Column

    balance = prevBalance - principalPaid

    if (balance > 0 && principalPaid <= 0) {
        return {
            error: 'Payment is too low. Balance will never be paid off.'
        }
    }

    return {
        paymentNumber,
        date,
        rate,
        payment,
        extraPayment,
        totalPayment,
        interestPaid,
        principalPaid,
        balance,
        prevBalance,
    }
}

export default CreditCardPaymentCalculator
